import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import serviceAPI from '../services/serviceAPI';
import toast from 'react-hot-toast';
import haircut from '../assets/haircut.jpg';
import beardTrimImg from '../assets/beardtrim.jpg';
import beardTrimAlt from '../assets/Beard Trim.jpg';
import beardTrimShaping from '../assets/beardtrishaping.jpg';
import completeGrooming from '../assets/completegroomingpackage.jpg';
import facial from '../assets/facial.jpg';
import facialTreatment from '../assets/facialtreatment.jpg';
import fullBodyMassage from '../assets/fulbodymassage.jpg';
import haircolor from '../assets/haircolor.jpg';
import headMassage from '../assets/headmassage.jpg';
import heroimg from '../assets/heroimg.jpg';
import highlights from '../assets/highlights.jpg';
import hydratingFacial from '../assets/hydratingfacial.jpg';
import premiumGroomingPackage from '../assets/premium grooming package.jpg';
import premiumHaircut from '../assets/premiumhaircut.jpg';


const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useSelector((state) => state.auth);

  const categories = [
    'all',
    'haircut',
    'beard-trim',
    'facial',
    'massage',
    'hair-color',
    'groom-package',
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter((s) => s.category === selectedCategory));
    }
  }, [selectedCategory, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await serviceAPI.getAllServices({ isActive: true });
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  // Map of imported images to try matching by filename/keyword
  const importedImages = [
    { key: 'haircut', img: haircut },
    { key: 'premiumhaircut', img: premiumHaircut },
    { key: 'beardtrim', img: beardTrimImg },
    { key: 'beard trim', img: beardTrimAlt },
    { key: 'beardtrishaping', img: beardTrimShaping },
    { key: 'facial', img: facial },
    { key: 'facialtreatment', img: facialTreatment },
    { key: 'hydratingfacial', img: hydratingFacial },
    { key: 'haircolor', img: haircolor },
    { key: 'highlights', img: highlights },
    { key: 'fullbodymassage', img: fullBodyMassage },
    { key: 'headmassage', img: headMassage },
    { key: 'completegroomingpackage', img: completeGrooming },
    { key: 'premiumgroomingpackage', img: premiumGroomingPackage },
  ];

  const normalize = (str = '') => str.toLowerCase().replace(/[^a-z0-9]/g, '');

  const getImageForService = (service) => {
    // First priority: Use Cloudinary image URL if available
    if (service.image && service.image.startsWith('http')) {
      return service.image;
    }

    // Fallback to local images for backward compatibility
    const name = normalize(service.name || service.category || '');
    // Try exact or partial matches
    let found = importedImages.find((it) => name.includes(normalize(it.key)) || normalize(it.key).includes(name));
    if (found) return found.img;

    // Try token matching
    const tokens = (service.name || service.category || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    for (const t of tokens) {
      found = importedImages.find((it) => normalize(it.key).includes(t));
      if (found) return found.img;
    }

    // fallback
    return heroimg;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-center mb-4 dark:text-white">Our Services</h1>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
          Choose from our wide range of professional grooming and salon services
        </p>

        {/* Category Filter - Modern Tab Style */}
        <div className="mb-12">
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-2 border border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`relative px-6 py-3 rounded-xl font-semibold capitalize transition-all duration-300 transform ${
                      selectedCategory === cat
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                        : 'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105'
                    }`}
                  >
                    {selectedCategory === cat && (
                      <span className="absolute inset-0 rounded-xl bg-blue-600 opacity-20 blur-lg"></span>
                    )}
                    <span className="relative flex items-center gap-2">
                      {cat === 'all' && '🎯'}
                      {cat === 'haircut' && '✂️'}
                      {cat === 'beard-trim' && '🧔'}
                      {cat === 'facial' && '🧖'}
                      {cat === 'massage' && '💆'}
                      {cat === 'hair-color' && '🎨'}
                      {cat === 'groom-package' && '💼'}
                      {cat === 'all' ? 'All Services' : cat.replace('-', ' ')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        {loading ? (
          <p className="text-center">Loading services...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <div key={service._id} className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-transform duration-300 ease-out transform hover:-translate-y-1">
                <div className="absolute inset-0 h-44 overflow-hidden bg-gray-200">
                  <img src={getImageForService(service)} alt={service.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white dark:to-gray-800"></div>
                </div>

                <div className="relative pt-48 px-6 pb-6">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{service.name}</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{service.description}</p>

                  <div className="space-y-2 mb-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400">⏱️ Duration: {service.duration} minutes</p>
                    {service.discountPercentage > 0 && (
                      <p className="text-sm text-green-600 font-semibold">🎉 {service.discountPercentage}% Off</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      {service.discountPercentage > 0 ? (
                        <div>
                          <span className="text-gray-400 line-through">₹{service.price}</span>
                          <p className="text-2xl font-bold text-blue-600">₹{service.discountPrice}</p>
                        </div>
                      ) : (
                        <p className="text-2xl font-bold text-blue-600">₹{service.price}</p>
                      )}
                    </div>
                    <a href={isAuthenticated ? '/booking' : '/signup'} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-500 no-underline hover:no-underline">Book Now</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredServices.length === 0 && (
          <p className="text-center text-gray-600 dark:text-gray-400">
            No services available in this category
          </p>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
